/**
 * useNavV2 — URL-canonical navigation helper for Navigation V2
 *
 * Thin wrapper over react-router. Reads profileId from path params or query params.
 * Does NOT maintain independent state — URL is the sole source of truth.
 *
 * Precedence: useParams().profileId > searchParams.get('profileId') > null
 */
import { useParams, useSearchParams, useLocation } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Route builders — canonical URL construction
// ---------------------------------------------------------------------------

type AppKey = 'screen' | 'learn' | 'audio-digests' | 'litscholar' | 'community' | 'litportal';

const APP_ROOT: Record<AppKey, string> = {
  'screen': '/screen',
  /** LitHub — digest/article routes live under `/library`. */
  'learn': '/library',
  'audio-digests': '/audio-digests',
  'litscholar': '/litscholar',
  'community': '/community',
  'litportal': '/litportal',
};

const LIST_SEGMENT: Record<AppKey, string> = {
  'screen': 'queues',
  'learn': 'digests',
  'audio-digests': 'digests',
  'litscholar': 'digests',
  'community': 'digests',
  'litportal': 'digests',
};

export const navV2Routes = {
  /** /screen/queues or /library/digests */
  index: (app: AppKey): string =>
    `${APP_ROOT[app]}/${LIST_SEGMENT[app]}`,

  /** /screen/queues/:profileId or /library/digests/:profileId */
  profileDetail: (app: AppKey, profileId: string): string =>
    `${APP_ROOT[app]}/${LIST_SEGMENT[app]}/${profileId}`,

  /** /screen/articles/:pmid or /library/articles/:pmid */
  articleDetail: (app: AppKey, pmid: string, profileId?: string, origin?: string): string => {
    const base = `${APP_ROOT[app]}/articles/${pmid}`;
    const params = new URLSearchParams();
    if (profileId) params.set('profileId', profileId);
    if (origin) params.set('from', origin);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  },

  /** Root route for an app (LitHub shelf, not the home hub). */
  root: (app: AppKey): string =>
    app === 'learn' ? '/library/shelf' : APP_ROOT[app],
};

// ---------------------------------------------------------------------------
// Hook — reads current navigation context from URL
// ---------------------------------------------------------------------------

export function useNavV2() {
  const params = useParams<{ profileId?: string; pmid?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Precedence: path param > query param > null
  const profileId = params.profileId || searchParams.get('profileId') || null;
  const pmid = params.pmid || null;
  const originApp = searchParams.get('from') || null;

  // Detect current app from pathname
  const currentApp: AppKey | null =
    location.pathname.startsWith('/screen') ? 'screen' :
    location.pathname.startsWith('/library') || location.pathname.startsWith('/learn') ? 'learn' :
    location.pathname.startsWith('/audio-digests') ? 'audio-digests' :
    location.pathname.startsWith('/litscholar') ? 'litscholar' :
    location.pathname.startsWith('/community') ? 'community' :
    location.pathname.startsWith('/litportal') ? 'litportal' :
    null;

  return { profileId, pmid, originApp, currentApp };
}

export type { AppKey };
